import { unref, onMounted, computed, ref, watch } from 'vue';
import type { MaybeRef } from 'vue';
import type { Schema } from 'yup';
import { watchDebounced } from '@vueuse/core';
import { defaultValidationOptions } from '../constants/default-validation-options';
import type { ValidationResult } from '../types';
import type { ValidationOptions } from '../types/validation-options';

export const useValidation = <TValue = unknown>(
  value: MaybeRef<TValue>,
  rules?: Schema<TValue> | ((value: TValue) => string),
  validationOptions: ValidationOptions = defaultValidationOptions
) => {
  const options: ValidationOptions = { ...defaultValidationOptions, ...validationOptions };
  type Errors = string[];
  const errors = ref<Errors>([]);
  const errorMessage = computed(() => errors.value[0] || '');
  const valid = computed(() => !errorMessage.value && validated.value);
  const validated = ref(false);

  const validate = (onlySuccess = false): ValidationResult => {
    if (typeof rules === 'object' && rules['validateSync']) {
      try {
        (rules as Schema<TValue>).validateSync(unref(value), { abortEarly: false });
        errors.value = [];
      } catch (e) {
        if (onlySuccess && typeof onlySuccess === 'boolean') return;
        errors.value = e.errors;
      }
    }

    if (typeof rules === 'function') {
      const newError = rules(unref(value));
      if (!newError) {
        errors.value = [];
      } else if (!(onlySuccess && typeof onlySuccess === 'boolean')) {
        errors.value = [newError];
      }
    }

    validated.value = true;

    return {
      errors: errors.value,
      errorMessage: errorMessage.value,
      valid: valid.value,
      validated: validated.value,
    };
  };

  const reset = () => {
    errors.value = [];
    validated.value = false;
  };

  watch(ref(value), () => {
    if (options.resetOnUpdate) {
      reset();
    }
  });

  watchDebounced(
    ref(value),
    () => {
      if (options.validateSuccessOnUpdate) {
        validate(true);
        return;
      }
      if (options.validateOnUpdate) {
        validate();
      }
    },
    { debounce: options.debounce }
  );

  onMounted(() => {
    if (options.validateOnMount) {
      validate();
    }
  });

  return {
    validated: computed(() => validated.value),
    errors: computed(() => errors.value),
    errorMessage,
    valid,
    validate,
    reset,
  };
};
